import { ColumnActionsMode, IColumn, Link as FluentLink } from "@fluentui/react";
import React from "react";
import { IStacCollection } from "types/stac";
import { CollectionActions } from "./Catalog.Collection.Actions";
import { ISortBy } from "pages/Explore/types";
import { formatDateShort } from "pages/Explore/utils/time";
import { CatalogCollectionThumbnail } from "./Catalog.Thumbnail";
import { LinkWithParams } from "utils/components/LinkWithParams";
import { getCollectionDetailUrl } from "./helpers";

export const getCollectionColumns = (
  collectionRenderFn: (collection: IStacCollection) => JSX.Element | null,
  itemsAsButton: boolean,
  onButtonClick: (collectionId: string) => void,
  sortInfo: ISortBy,
  isEditable: boolean,
  onEdit?: (collection: IStacCollection) => void
): IColumn[] => {
  const coreColumns = [
    {
      key: "image",
      name: "Image",
      minWidth: 150,
      maxWidth: 270,
      onRender: (collection: IStacCollection) => {
        return itemsAsButton ? (
          <FluentLink onClick={() => onButtonClick(collection.id)}>
            <CatalogCollectionThumbnail
              key={collection.id}
              collectionId={collection.id}
              assets={collection.assets || {}}
            />
          </FluentLink>
        ) : (
          <LinkWithParams
            to={getCollectionDetailUrl(collection.id)}
            data-cy="catalog-collection-thumb-link"
          >
            <CatalogCollectionThumbnail
              collectionId={collection.id}
              assets={collection.assets || {}}
            />
          </LinkWithParams>
        );
      },
    },
    {
      key: "title",
      name: "Collection name",
      minWidth: 150,
      maxWidth: 300,
      onRender: (collection: IStacCollection) => {
        return (
          <VerticalCenterColumn>
            {itemsAsButton ? (
              <FluentLink
                onClick={() => onButtonClick(collection.id)}
                data-cy="catalog-collection-item"
                key={collection.id}
              >
                {collection.title || collection.id}
              </FluentLink>
            ) : (
              <LinkWithParams
                to={getCollectionDetailUrl(collection.id)}
                data-cy="catalog-collection-item"
              >
                {collection.title || collection.id}
              </LinkWithParams>
            )}
          </VerticalCenterColumn>
        );
      },
      isSorted: sortInfo.field === "title",
      isSortedDescending:
        sortInfo.field === "title" && sortInfo.direction === "desc",
    },
    {
      key: "description",
      name: "Description",
      minWidth: 300,
      onRender: (collection: IStacCollection) => {
        return (
          <span
            style={{
              display: "flex",
              flexDirection: "column",
              whiteSpace: "pre-wrap",
            }}
          >
            {truncateDescription(collection) || "-"}
          </span>
        );
      },
    },
    {
      key: "msft:_created",
      name: "Date created",
      minWidth: 150,
      onRender: (collection: IStacCollection) => {
        const created = collection["msft:_created"];
        return (
          <VerticalCenterColumn>
            {created ? formatDateShort(created) : "-"}
          </VerticalCenterColumn>
        );
      },
      isSorted: sortInfo.field === "msft:_created",
      isSortedDescending:
        sortInfo.field === "msft:_created" && sortInfo.direction === "desc",
    },
    // TODO: reenable when we support updated date reflecting new _items_ in the
    // collection, not just when collection metadata is edited.
    // See: https://dev.azure.com/msazure/One/_workitems/edit/26517426

    // {
    //   key: "msft:_updated",
    //   name: "Updated",
    //   minWidth: 150,
    //   onRender: (collection: IStacCollection) => {
    //     const updated = collection["msft:_updated"];
    //     return (
    //       <VerticalCenterColumn>
    //         {updated ? formatDatetimeHuman(updated) : "-"}
    //       </VerticalCenterColumn>
    //     );
    //   },
    //   isSorted: sortInfo.field === "msft:_updated",
    //   isSortedDescending:
    //     sortInfo.field === "msft:_updated" && sortInfo.direction === "desc",
    // },
  ];

  const actionColumns = [
    {
      key: "actions",
      name: "Actions",
      minWidth: 50,
      onRender: (item: any) => {
        return (
          <VerticalCenterColumn>
            {onEdit && <CollectionActions collection={item} onEdit={onEdit} />}
          </VerticalCenterColumn>
        );
      },
      columnActionsMode: ColumnActionsMode.disabled,
    },
  ];

  return isEditable ? [...coreColumns, ...actionColumns] : coreColumns;
};

const VerticalCenterColumn: React.FC = ({ children }) => {
  return <div style={{ height: "100%", display: "flex" }}>{children}</div>;
};

const truncateDescription = (collection: IStacCollection) => {
  const baseContent =
    collection?.["msft:short_description"] || collection.description;
  if (baseContent && baseContent.length > 300) {
    const lastSpaceIndex = baseContent.lastIndexOf(" ", 300);
    return `${baseContent.substring(0, lastSpaceIndex)}...`;
  }
  return baseContent || "";
};
